/**
 * Bank Transaction Allocation Component
 * 
 * Handles the workflow for allocating bank transactions to shipments:
 * 1. Enter transaction details (type, amount, date, reference)
 * 2. Search and allocate to shipments
 * 3. Apply available credits
 * 4. Validate allocations sum to transaction amount
 * 5. Save and optionally push to Xero
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Select,
  Button,
  Table,
  Space,
  Row,
  Col,
  Statistic,
  Tag,
  Modal,
  message,
  Alert,
  Divider,
  Tooltip,
  Empty,
  Spin,
  Typography,
  Popconfirm,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  SearchOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CloudUploadOutlined,
  SaveOutlined,
  BankOutlined,
  CreditCardOutlined,
  CopyOutlined,
  EditOutlined,
  CloseOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { bankTransactionsApi, creditsApi, xeroApi } from '../../services/api';
import api from '../../services/api';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// Types
interface Allocation {
  id?: string;
  shipmentId: string;
  shipmentRef: string;
  contractRef?: string;
  buyerName?: string;
  supplierName?: string;
  amount: number;
  allocationType: string;
  xeroInvoiceId?: string;
  xeroId?: string;
  invoiceNumber?: string;
  notes?: string;
  availableInvoices?: XeroInvoiceOption[];
}

interface AvailableCredit {
  id: string;
  creditType: string;
  originalAmount: number;
  remainingAmount: number;
  partyName: string;
  originShipment?: {
    reference: string;
  };
}

interface XeroInvoiceOption {
  id: string;
  invoiceNumber: string;
  total: number;
  amountDue: number;
  status: string;
  invoiceType: string;
}

interface ShipmentSearchResult {
  id: string;
  reference: string;
  contract?: {
    reference: string;
    buyer?: { code?: string; name: string };
    supplier?: { code?: string; name: string; commissionWithheldFromPayment?: boolean };
    commission_rate?: number;
    commission_per_lb?: boolean;
  };
  goods_summary?: {
    total_sale: number;
    total_lbs?: number;
  };
  invoice?: {
    date?: string;
    number?: string;
    amount?: number;
  };
  xeroInvoices?: XeroInvoiceOption[];
}

interface BankTransactionFormData {
  transactionType: string;
  amount: number;
  date: dayjs.Dayjs;
  reference?: string;
  counterparty?: string;
  description?: string;
  bankAccount?: string;
}

// Color constants
const colors = {
  success: '#169959',
  error: '#C32928',
  warning: '#E6B339',
  info: '#179DB5',
  textSecondary: '#8C8C8C',
};

// Transaction type options
const transactionTypes = [
  { value: 'WIRE_IN', label: 'Wire Transfer (Incoming)' },
  { value: 'WIRE_OUT', label: 'Wire Transfer (Outgoing)' },
  { value: 'CHECK_IN', label: 'Check (Incoming)' },
  { value: 'CHECK_OUT', label: 'Check (Outgoing)' },
  { value: 'ACH_IN', label: 'ACH (Incoming)' },
  { value: 'ACH_OUT', label: 'ACH (Outgoing)' },
];

const allocationTypes = [
  { value: 'INVOICE_PAYMENT', label: 'Invoice Payment' },
  { value: 'COMMISSION', label: 'Commission' },
  { value: 'DEPOSIT', label: 'Deposit/Advance' },
  { value: 'ADJUSTMENT', label: 'Adjustment' },
  { value: 'BANK_FEE', label: 'Bank Wire Fee' },
  { value: 'CREDIT_APPLIED', label: 'Credit Applied' },
  { value: 'OVERPAYMENT_CREDIT', label: 'Overpayment (Create Credit)' },
];

const CopyableText: React.FC<{
  value: string | null | undefined;
  children?: React.ReactNode;
}> = ({ value, children }) => {
  const handleCopy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      message.success(`Copied: ${value}`);
    } catch {
      message.error('Failed to copy');
    }
  };

  return (
    <span
      onClick={handleCopy}
      style={{
        cursor: value ? 'pointer' : 'default',
        borderRadius: 4,
        transition: 'background-color 0.2s',
      }}
      onMouseEnter={(e) => {
        if (value) e.currentTarget.style.backgroundColor = '#f0f0f0';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'transparent';
      }}
    >
      {children || value || '-'}
    </span>
  );
};

const CopyableValue: React.FC<{
  label: string;
  value: string | number | null | undefined;
  prefix?: string;
}> = ({ label, value, prefix = '' }) => {
  const [copied, setCopied] = useState(false);
  const displayValue = value ? `${prefix}${value}` : '-';
  const copyValue = value ? `${prefix}${value}` : '';

  const handleCopy = async () => {
    if (!copyValue) return;
    try {
      await navigator.clipboard.writeText(copyValue);
      setCopied(true);
      message.success(`Copied: ${copyValue}`);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      message.error('Failed to copy');
    }
  };

  return (
    <div style={{ marginBottom: 12 }}>
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
        {label}
      </Text>
      <div
        onClick={handleCopy}
        style={{
          cursor: copyValue ? 'pointer' : 'default',
          padding: '8px 12px',
          backgroundColor: '#fafafa',
          borderRadius: 6,
          border: '1px solid #f0f0f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          transition: 'all 0.2s',
        }}
        onMouseEnter={(e) => {
          if (copyValue) {
            e.currentTarget.style.backgroundColor = '#f0f0f0';
            e.currentTarget.style.borderColor = '#d9d9d9';
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#fafafa';
          e.currentTarget.style.borderColor = '#f0f0f0';
        }}
      >
        <Text strong style={{ fontSize: 14 }}>{displayValue}</Text>
        {copyValue && (
          <CopyOutlined style={{ color: copied ? colors.success : colors.textSecondary }} />
        )}
      </div>
    </div>
  );
};

// Running Total Display Component
const RunningTotalDisplay: React.FC<{
  transactionAmount: number;
  allocatedAmount: number;
}> = ({ transactionAmount, allocatedAmount }) => {
  const difference = transactionAmount - allocatedAmount;
  const isBalanced = Math.abs(difference) < 0.01;
  const isOver = difference < -0.01;

  return (
    <Card
      size="small"
      style={{
        borderColor: isBalanced ? colors.success : isOver ? colors.error : colors.warning,
        borderWidth: 2,
      }}
    >
      <Row gutter={16} align="middle">
        <Col span={8}>
          <Statistic
            title="Transaction Amount"
            value={transactionAmount}
            precision={2}
            prefix="$"
          />
        </Col>
        <Col span={8}>
          <Statistic
            title="Allocated"
            value={allocatedAmount}
            precision={2}
            prefix="$"
            valueStyle={{ color: allocatedAmount > 0 ? colors.info : undefined }}
          />
        </Col>
        <Col span={8}>
          <Statistic
            title="Difference"
            value={Math.abs(difference)}
            precision={2}
            prefix={isBalanced ? '' : difference > 0 ? '-$' : '+$'}
            valueStyle={{
              color: isBalanced ? colors.success : isOver ? colors.error : colors.warning,
            }}
            suffix={
              isBalanced ? (
                <CheckCircleOutlined style={{ color: colors.success, marginLeft: 8 }} />
              ) : (
                <ExclamationCircleOutlined
                  style={{ color: isOver ? colors.error : colors.warning, marginLeft: 8 }}
                />
              )
            }
          />
        </Col>
      </Row>
      {!isBalanced && (
        <Alert
          message={
            isOver
              ? `Over-allocated by $${Math.abs(difference).toFixed(2)}`
              : `Under-allocated by $${Math.abs(difference).toFixed(2)}`
          }
          type={isOver ? 'error' : 'warning'}
          showIcon
          style={{ marginTop: 12 }}
        />
      )}
    </Card>
  );
};

// Shipment Search Modal Component
const ShipmentSearchModal: React.FC<{
  visible: boolean;
  onClose: () => void;
  onSelect: (shipment: ShipmentSearchResult) => void;
  excludeIds: string[];
}> = ({ visible, onClose, onSelect, excludeIds }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const { data: searchResults, isLoading } = useQuery({
    queryKey: ['shipment-search', searchTerm],
    queryFn: async () => {
      if (!searchTerm || searchTerm.length < 2) return { shipments: [] };
      return api.get('/shipments', {
        params: { search: searchTerm, limit: 20 },
      }) as any;
    },
    enabled: searchTerm.length >= 2,
    staleTime: 30000,
  });

  const shipments = (searchResults?.shipments || []).filter(
    (s: ShipmentSearchResult) => !excludeIds.includes(s.id)
  );

  return (
    <Modal
      title="Search Shipment"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={700}
    >
      <Input.Search
        placeholder="Search by shipment ref, contract ref, or buyer/supplier..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        allowClear
        style={{ marginBottom: 16 }}
      />

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin />
        </div>
      ) : shipments.length === 0 ? (
        <Empty
          description={
            searchTerm.length < 2
              ? 'Enter at least 2 characters to search'
              : 'No shipments found'
          }
        />
      ) : (
        <Table
          dataSource={shipments}
          rowKey="id"
          size="small"
          pagination={false}
          columns={[
            {
              title: 'Shipment',
              dataIndex: 'reference',
              key: 'reference',
              render: (ref: string, record: ShipmentSearchResult) => (
                <div>
                  <div style={{ fontWeight: 600 }}>{ref}</div>
                  <div style={{ fontSize: 11, color: colors.textSecondary }}>
                    {record.contract?.reference}
                  </div>
                </div>
              ),
            },
            {
              title: 'Buyer',
              key: 'buyer',
              render: (_: unknown, record: ShipmentSearchResult) =>
                record.contract?.buyer?.name || '-',
            },
            {
              title: 'Supplier',
              key: 'supplier',
              render: (_: unknown, record: ShipmentSearchResult) =>
                record.contract?.supplier?.name || '-',
            },
            {
              title: 'Action',
              key: 'action',
              width: 80,
              render: (_: unknown, record: ShipmentSearchResult) => (
                <Button
                  type="primary"
                  size="small"
                  onClick={() => {
                    onSelect(record);
                    onClose();
                    setSearchTerm('');
                  }}
                >
                  Select
                </Button>
              ),
            },
          ]}
        />
      )}
    </Modal>
  );
};

// Available Credits Panel Component
const AvailableCreditsPanel: React.FC<{
  partyName?: string;
  onApplyCredit: (credit: AvailableCredit, amount: number) => void;
}> = ({ partyName, onApplyCredit }) => {
  const [applyingCredit, setApplyingCredit] = useState<AvailableCredit | null>(null);
  const [applyAmount, setApplyAmount] = useState<number>(0);

  const { data: creditsData, isLoading } = useQuery({
    queryKey: ['available-credits', partyName],
    queryFn: async () => {
      return creditsApi.getAvailable({
        partyName: partyName || undefined,
      }) as any;
    },
    enabled: true,
    staleTime: 30000,
  });

  const credits = creditsData?.credits || [];

  if (isLoading) {
    return (
      <Card title="Available Credits" size="small">
        <Spin />
      </Card>
    );
  }

  if (credits.length === 0) {
    return (
      <Card title="Available Credits" size="small">
        <Empty description="No available credits" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </Card>
    );
  }

  return (
    <Card
      title={
        <Space>
          <CreditCardOutlined />
          <span>Available Credits</span>
          <Tag color="blue">{credits.length}</Tag>
        </Space>
      }
      size="small"
    >
      <Table
        dataSource={credits}
        rowKey="id"
        size="small"
        pagination={false}
        columns={[
          {
            title: 'Type',
            dataIndex: 'creditType',
            key: 'creditType',
            width: 100,
            render: (type: string) => <Tag>{type}</Tag>,
          },
          {
            title: 'Party',
            dataIndex: 'partyName',
            key: 'partyName',
            ellipsis: true,
          },
          {
            title: 'Remaining',
            dataIndex: 'remainingAmount',
            key: 'remainingAmount',
            width: 100,
            align: 'right',
            render: (amount: number) => (
              <span style={{ color: colors.success, fontWeight: 600 }}>
                ${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
            ),
          },
          {
            title: 'Action',
            key: 'action',
            width: 80,
            render: (_: unknown, record: AvailableCredit) => (
              <Button
                type="link"
                size="small"
                onClick={() => {
                  setApplyingCredit(record);
                  setApplyAmount(Number(record.remainingAmount));
                }}
              >
                Apply
              </Button>
            ),
          },
        ]}
      />

      {/* Apply Credit Modal */}
      <Modal
        title="Apply Credit"
        open={!!applyingCredit}
        onCancel={() => setApplyingCredit(null)}
        onOk={() => {
          if (applyingCredit && applyAmount > 0) {
            onApplyCredit(applyingCredit, applyAmount);
            setApplyingCredit(null);
          }
        }}
      >
        {applyingCredit && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <Text type="secondary">Credit Type:</Text>{' '}
              <Tag>{applyingCredit.creditType}</Tag>
            </div>
            <div>
              <Text type="secondary">Party:</Text> {applyingCredit.partyName}
            </div>
            <div>
              <Text type="secondary">Available:</Text>{' '}
              <Text strong style={{ color: colors.success }}>
                ${Number(applyingCredit.remainingAmount).toFixed(2)}
              </Text>
            </div>
            <Divider />
            <Form.Item label="Amount to Apply">
              <InputNumber
                value={applyAmount}
                onChange={(value) => setApplyAmount(value || 0)}
                min={0.01}
                max={Number(applyingCredit.remainingAmount)}
                precision={2}
                prefix="$"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Space>
        )}
      </Modal>
    </Card>
  );
};

// Xero Push Confirmation Dialog
const XeroPushDialog: React.FC<{
  visible: boolean;
  transactionId: string;
  onClose: () => void;
  onConfirm: () => void;
}> = ({ visible, transactionId, onClose, onConfirm }) => {
  const { data: preview, isLoading } = useQuery({
    queryKey: ['xero-preview', transactionId],
    queryFn: async () => {
      return bankTransactionsApi.previewXeroPush(transactionId) as any;
    },
    enabled: visible && !!transactionId,
  });

  return (
    <Modal
      title={
        <Space>
          <CloudUploadOutlined />
          <span>Push to Xero</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      okText="Push to Xero"
      okButtonProps={{
        disabled: !preview?.canPush,
        loading: isLoading,
        danger: false,
        type: 'primary',
      }}
      onOk={onConfirm}
      width={600}
    >
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin />
          <div style={{ marginTop: 16 }}>Loading preview...</div>
        </div>
      ) : !preview ? (
        <Alert message="Failed to load preview" type="error" />
      ) : (
        <Space direction="vertical" style={{ width: '100%' }}>
          {!preview.canPush && preview.blockers?.length > 0 && (
            <Alert
              message="Cannot Push to Xero"
              description={
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {preview.blockers.map((blocker: string, i: number) => (
                    <li key={i}>{blocker}</li>
                  ))}
                </ul>
              }
              type="error"
              showIcon
            />
          )}

          <Card size="small" title="Transaction">
            <Row gutter={16}>
              <Col span={12}>
                <Text type="secondary">Amount:</Text>{' '}
                <Text strong>${preview.transactionAmount?.toFixed(2)}</Text>
              </Col>
              <Col span={12}>
                <Text type="secondary">Date:</Text> {preview.transactionDate}
              </Col>
            </Row>
          </Card>

          <Card size="small" title={`Allocations (${preview.allocations?.length || 0})`}>
            {preview.allocations?.map((alloc: any, i: number) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '4px 0',
                  borderBottom: i < preview.allocations.length - 1 ? '1px solid #f0f0f0' : undefined,
                }}
              >
                <span>
                  {alloc.shipmentReference}
                  {alloc.invoiceNumber && (
                    <Tag style={{ marginLeft: 8 }}>{alloc.invoiceNumber}</Tag>
                  )}
                </span>
                <span style={{ fontWeight: 600 }}>${alloc.amount?.toFixed(2)}</span>
              </div>
            ))}
          </Card>

          {preview.credits?.length > 0 && (
            <Card size="small" title={`Credits Applied (${preview.credits.length})`}>
              {preview.credits.map((credit: any, i: number) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>
                    <Tag>{credit.creditType}</Tag> {credit.partyName}
                  </span>
                  <span>${credit.amount?.toFixed(2)}</span>
                </div>
              ))}
            </Card>
          )}

          {preview.canPush && (
            <Alert
              message="Ready to Push"
              description="This will create payment record(s) in Xero for each allocation with a linked invoice."
              type="success"
              showIcon
            />
          )}
        </Space>
      )}
    </Modal>
  );
};

const InvoiceSelect: React.FC<{
  shipmentId: string;
  value?: string;
  onChange: (invoiceId: string | undefined, invoice?: XeroInvoiceOption) => void;
  initialOptions?: XeroInvoiceOption[];
}> = ({ shipmentId, value, onChange, initialOptions }) => {
  const [enabled, setEnabled] = useState(false);
  
  const { data, isLoading } = useQuery({
    queryKey: ['shipment-invoices', shipmentId],
    queryFn: async () => {
      if (!shipmentId) return { invoices: [] };
      return xeroApi.getShipmentInvoices(shipmentId) as any;
    },
    enabled: enabled && !!shipmentId,
    staleTime: 60000,
  });

  const invoices = data?.invoices || initialOptions || [];

  return (
    <Select
      value={value}
      size="small"
      style={{ width: '100%' }}
      placeholder={value ? "Select invoice" : "Search/Select invoice"}
      loading={isLoading}
      allowClear
      showSearch
      onDropdownVisibleChange={(open) => {
        if (open) setEnabled(true);
      }}
      filterOption={(input, option) => {
        const label = option?.label || option?.value;
        if (typeof label === 'string') {
          return label.toLowerCase().includes(input.toLowerCase());
        }
        return false;
      }}
      onChange={(val) => {
        const invoice = invoices.find((i: XeroInvoiceOption) => i.id === val);
        onChange(val, invoice);
      }}
    >
      {invoices.map((inv: XeroInvoiceOption) => (
        <Option key={inv.id} value={inv.id}>
          {inv.invoiceNumber} (${inv.amountDue?.toLocaleString()})
        </Option>
      ))}
    </Select>
  );
};

// Initial data for pre-populating from bank transaction matching
interface InitialTransactionData {
  transactionType?: string;
  amount?: number;
  date?: string;
  reference?: string;
  counterparty?: string;
  description?: string;
  bankAccount?: string;
}

interface InitialShipmentAllocation {
  shipmentId: string;
  shipmentRef: string;
  contractRef?: string;
  buyerName?: string;
  supplierName?: string;
  estimatedValue?: number;
  commissionWithheldFromPayment?: boolean;
  commissionRate?: number | null;
}

// Main Component
const BankTransactionAllocation: React.FC<{
  transactionId?: string;
  initialData?: InitialTransactionData;
  initialShipment?: InitialShipmentAllocation;
  initialShipments?: InitialShipmentAllocation[];
  onSaved?: () => void;
}> = ({ transactionId, initialData, initialShipment, initialShipments, onSaved }) => {
  const queryClient = useQueryClient();
  const [form] = Form.useForm<BankTransactionFormData>();

  // State
  const [allocations, setAllocations] = useState<Allocation[]>([]);
  const [shipmentSearchVisible, setShipmentSearchVisible] = useState(false);
  const [xeroPushVisible, setXeroPushVisible] = useState(false);
  const [savedTransactionId, setSavedTransactionId] = useState<string | null>(transactionId || null);
  const [isEditing, setIsEditing] = useState(!transactionId);

  // Fetch existing transaction if editing
  const { data: existingTransaction, isLoading: loadingTransaction } = useQuery({
    queryKey: ['bank-transaction', transactionId],
    queryFn: async () => {
      return bankTransactionsApi.get(transactionId!) as any;
    },
    enabled: !!transactionId,
  });

  const { data: bankAccountsData } = useQuery({
    queryKey: ['xero-bank-accounts'],
    queryFn: async () => {
      const response = await xeroApi.getBankAccounts();
      return response as unknown as {
        accounts: Array<{
          id: string;
          code: string | null;
          name: string;
          bankAccountNumber: string | null;
          bankAccountType: string | null;
          currencyCode: string | null;
          enablePaymentsToAccount: boolean;
        }>;
      };
    },
    staleTime: 5 * 60 * 1000,
  });

  const bankAccounts = bankAccountsData?.accounts || [];
  const defaultBankAccount = bankAccounts.find(
    (account) => account.name.includes('Bank of America') && account.name.includes('5089')
  );

  useEffect(() => {
    if (defaultBankAccount && !form.getFieldValue('bankAccount') && !existingTransaction && !initialData?.bankAccount) {
      form.setFieldsValue({ bankAccount: defaultBankAccount.id });
    }
  }, [defaultBankAccount, existingTransaction, initialData, form]);

  // Populate form and allocations when loading existing transaction
  useEffect(() => {
    if (existingTransaction) {
      form.setFieldsValue({
        transactionType: existingTransaction.transactionType,
        amount: Number(existingTransaction.amount),
        date: dayjs(existingTransaction.date),
        reference: existingTransaction.reference || undefined,
        counterparty: existingTransaction.counterparty || undefined,
        description: existingTransaction.description || undefined,
        bankAccount: existingTransaction.bankAccount || undefined,
      });

      const isMatchFlow = resolvedInitialShipments.length > 0;
      if (existingTransaction.allocations && !isMatchFlow) {
        setAllocations(
          existingTransaction.allocations.map((a: any) => {
            const isFee = a.allocationType === 'BANK_FEE' || a.allocationType === 'ADJUSTMENT';
            return {
              id: a.id,
              shipmentId: a.shipmentId,
              shipmentRef: a.shipment?.reference || (isFee ? '' : 'Unknown'),
              contractRef: a.shipment?.contract?.reference,
              buyerName: a.shipment?.contract?.buyer?.name || a.shipment?.contract?.buyer?.shortName,
              supplierName: a.shipment?.contract?.supplier?.name || a.shipment?.contract?.supplier?.shortName,
              amount: Number(a.amount),
              allocationType: a.allocationType,
              xeroInvoiceId: a.xeroInvoiceId,
              xeroId: a.xeroInvoice?.xeroId,
              invoiceNumber: a.xeroInvoice?.invoiceNumber,
              notes: a.notes,
            };
          })
        );
      }
    }
  }, [existingTransaction, form, initialShipment]);

  // Pre-populate from initialData (bank transaction matching flow)
  useEffect(() => {
    if (initialData && !transactionId) {
      form.setFieldsValue({
        transactionType: initialData.transactionType,
        amount: initialData.amount,
        date: initialData.date ? dayjs(initialData.date) : undefined,
        reference: initialData.reference,
        counterparty: initialData.counterparty,
        description: initialData.description,
        bankAccount: initialData.bankAccount,
      });
    }
  }, [initialData, transactionId, form]);

  // Pre-add shipment allocations from matching and auto-save to backend
  const [matchAllocsSaved, setMatchAllocsSaved] = useState(false);
  const resolvedInitialShipments = initialShipments || (initialShipment ? [initialShipment] : []);

  useEffect(() => {
    if (resolvedInitialShipments.length === 0 || allocations.length > 0) return;

    const newAllocations: Allocation[] = [];

    for (const shipment of resolvedInitialShipments) {
      const invoiceAmount = shipment.estimatedValue || 0;
      newAllocations.push({
        shipmentId: shipment.shipmentId,
        shipmentRef: shipment.shipmentRef,
        contractRef: shipment.contractRef,
        buyerName: shipment.buyerName,
        supplierName: shipment.supplierName,
        amount: invoiceAmount,
        allocationType: 'INVOICE_PAYMENT',
      });

      // Auto-split: only for outgoing payments to suppliers with commission withheld
      const commissionRate = shipment.commissionRate;
      if (isOutgoingTransaction() && shipment.commissionWithheldFromPayment && commissionRate && commissionRate > 0 && invoiceAmount > 0) {
        const commissionAmount = invoiceAmount * (commissionRate / 100);
        newAllocations.push({
          shipmentId: shipment.shipmentId,
          shipmentRef: shipment.shipmentRef,
          contractRef: shipment.contractRef,
          buyerName: shipment.buyerName,
          supplierName: shipment.supplierName,
          amount: -Math.round(commissionAmount * 100) / 100,
          allocationType: 'COMMISSION',
          notes: `Commission withheld @ ${commissionRate}%`,
        });
        message.info(`Auto-added commission withholding of $${commissionAmount.toFixed(2)} (${commissionRate}%)`);
      }
    }

    setAllocations(newAllocations);
  }, [resolvedInitialShipments.length]);

  // Auto-save match allocations to backend once we have a transaction ID
  useEffect(() => {
    if (resolvedInitialShipments.length === 0 || matchAllocsSaved || !savedTransactionId) return;
    const unsaved = allocations.filter((a) => !a.id);
    if (unsaved.length === 0) return;

    setMatchAllocsSaved(true);
    const saveAllocations = async () => {
      for (const alloc of unsaved) {
        try {
          const savedAlloc = await addAllocationMutation.mutateAsync({
            txId: savedTransactionId,
            allocation: alloc,
          });
          if (savedAlloc?.id) {
            setAllocations((prev) =>
              prev.map((a) => (a === alloc ? { ...a, id: savedAlloc.id } : a))
            );
          }
        } catch {
          // Error already handled by mutation onError
        }
      }
      queryClient.invalidateQueries({ queryKey: ['bank-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['bank-transaction', savedTransactionId] });
    };
    saveAllocations();
  }, [resolvedInitialShipments.length, savedTransactionId, allocations, matchAllocsSaved]);

  const isOutgoingTransaction = () => {
    const type = existingTransaction?.transactionType || form.getFieldValue('transactionType') || '';
    return type.endsWith('_OUT');
  };

  // Calculate totals - use existingTransaction for view mode, form for edit mode
  const formAmount = Form.useWatch('amount', form);
  const transactionAmount = existingTransaction?.amount ? Number(existingTransaction.amount) : (formAmount || 0);
  const allocatedAmount = useMemo(
    () => allocations.reduce((sum, a) => sum + a.amount, 0),
    [allocations]
  );
  const isBalanced = Math.abs(transactionAmount - allocatedAmount) < 0.01;

  const createMutation = useMutation({
    mutationFn: async (data: BankTransactionFormData) => {
      const transaction = await bankTransactionsApi.create({
        transactionType: data.transactionType as any,
        amount: data.amount,
        date: data.date.toISOString(),
        reference: data.reference,
        counterparty: data.counterparty,
        description: data.description,
        bankAccount: data.bankAccount,
      });
      return transaction as any;
    },
    onSuccess: (data) => {
      setSavedTransactionId(data.id);
      message.success('Transaction created');
    },
    onError: (error: Error) => {
      message.error(`Failed to create transaction: ${error.message}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: BankTransactionFormData }) => {
      const transaction = await bankTransactionsApi.update(id, {
        transactionType: data.transactionType as any,
        amount: data.amount,
        date: data.date.toISOString(),
        reference: data.reference || null,
        counterparty: data.counterparty || null,
        description: data.description || null,
        bankAccount: data.bankAccount || null,
      });
      return transaction as any;
    },
    onError: (error: Error) => {
      message.error(`Failed to update transaction: ${error.message}`);
    },
  });

  const addAllocationMutation = useMutation({
    mutationFn: async ({
      txId,
      allocation,
    }: {
      txId: string;
      allocation: Omit<Allocation, 'id'>;
    }) => {
      return bankTransactionsApi.addAllocation(txId, {
        shipmentId: allocation.shipmentId,
        amount: allocation.amount,
        allocationType: allocation.allocationType as any,
        xeroInvoiceId: allocation.xeroInvoiceId,
        notes: allocation.notes,
      }) as any;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bank-transaction', savedTransactionId] });
    },
    onError: (error: Error) => {
      message.error(`Failed to add allocation: ${error.message}`);
    },
  });

  // Remove allocation mutation
  const removeAllocationMutation = useMutation({
    mutationFn: async ({ txId, allocId }: { txId: string; allocId: string }) => {
      await bankTransactionsApi.removeAllocation(txId, allocId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bank-transaction', savedTransactionId] });
    },
    onError: (error: Error) => {
      message.error(`Failed to remove allocation: ${error.message}`);
    },
  });

  const pushToXeroMutation = useMutation({
    mutationFn: async (txId: string) => {
      return bankTransactionsApi.pushToXero(txId) as any;
    },
    onSuccess: (data) => {
      setXeroPushVisible(false);
      if (data.success) {
        message.success(`Pushed to Xero! Created ${data.xeroPaymentIds?.length || 0} payment(s)`);
        queryClient.invalidateQueries({ queryKey: ['bank-transaction', savedTransactionId] });
        onSaved?.();
      } else {
        message.error(`Push failed: ${data.errors?.join(', ')}`);
      }
    },
    onError: (error: Error) => {
      message.error(`Failed to push to Xero: ${error.message}`);
    },
  });

  const handleAddShipment = (shipment: ShipmentSearchResult) => {
    const primaryInvoice = shipment.xeroInvoices?.[0];
    const invoiceAmount = primaryInvoice?.amountDue || primaryInvoice?.total || shipment.invoice?.amount || shipment.goods_summary?.total_sale || 0;

    const newAllocation: Allocation = {
      shipmentId: shipment.id,
      shipmentRef: shipment.reference,
      contractRef: shipment.contract?.reference,
      buyerName: shipment.contract?.buyer?.name,
      supplierName: shipment.contract?.supplier?.name,
      amount: invoiceAmount,
      allocationType: 'INVOICE_PAYMENT',
      xeroInvoiceId: primaryInvoice?.id,
      invoiceNumber: primaryInvoice?.invoiceNumber || shipment.invoice?.number,
      availableInvoices: shipment.xeroInvoices,
    };

    const newAllocations = [newAllocation];

    // Auto-split: only for outgoing payments to suppliers with commission withheld
    const supplier = shipment.contract?.supplier;
    const commissionRate = shipment.contract?.commission_rate;
    if (isOutgoingTransaction() && supplier?.commissionWithheldFromPayment && commissionRate && commissionRate > 0 && invoiceAmount > 0) {
      const commissionAmount = invoiceAmount * (commissionRate / 100);
      const commissionAllocation: Allocation = {
        shipmentId: shipment.id,
        shipmentRef: shipment.reference,
        contractRef: shipment.contract?.reference,
        buyerName: shipment.contract?.buyer?.name,
        supplierName: supplier.name,
        amount: -Math.round(commissionAmount * 100) / 100,
        allocationType: 'COMMISSION',
        notes: `Commission withheld @ ${commissionRate}%`,
      };
      newAllocations.push(commissionAllocation);
      message.info(`Auto-added commission withholding of $${commissionAmount.toFixed(2)} (${commissionRate}%)`);
    }

    if (savedTransactionId) {
      for (const alloc of newAllocations) {
        addAllocationMutation.mutate({ txId: savedTransactionId, allocation: alloc });
      }
    }

    setAllocations([...allocations, ...newAllocations]);
  };

  const handleAddBankFee = () => {
    const difference = transactionAmount - allocatedAmount;
    const suggestedFee = difference < 0 ? Math.abs(difference) : 15;
    
    const newAllocation: Allocation = {
      shipmentId: '',
      shipmentRef: 'Bank Wire Fee',
      amount: -suggestedFee,
      allocationType: 'BANK_FEE',
      notes: 'Wire transfer fee',
    };

    setAllocations([...allocations, newAllocation]);
  };

  // Handle removing an allocation
  const handleRemoveAllocation = (index: number) => {
    const allocation = allocations[index];

    if (savedTransactionId && allocation?.id) {
      removeAllocationMutation.mutate({ txId: savedTransactionId, allocId: allocation.id });
    }

    setAllocations(allocations.filter((_, i) => i !== index));
  };

  // Handle updating allocation amount
  const handleUpdateAmount = (index: number, amount: number) => {
    const updated = [...allocations];
    if (updated[index]) {
      updated[index].amount = amount;
      setAllocations(updated);
    }
  };

  // Handle applying a credit
  const handleApplyCredit = (credit: AvailableCredit, amount: number) => {
    const newAllocation: Allocation = {
      shipmentId: credit.originShipment?.reference ? '' : '', // Credits don't always have shipment
      shipmentRef: `Credit: ${credit.creditType}`,
      amount: -amount, // Credits are negative
      allocationType: 'CREDIT_APPLIED',
      notes: `Applied from ${credit.partyName}`,
    };

    setAllocations([...allocations, newAllocation]);
    message.success(`Applied credit of $${amount.toFixed(2)}`);
  };

  const handleSave = async (): Promise<string | null> => {
    try {
      const values = await form.validateFields();

      let txId: string;

      if (savedTransactionId) {
        txId = savedTransactionId;
        await updateMutation.mutateAsync({ id: txId, data: values });
      } else {
        const result = await createMutation.mutateAsync(values);
        txId = result.id;
        setSavedTransactionId(txId);
      }

      const newAllocations = allocations.filter((a) => !a.id);
      for (const alloc of newAllocations) {
        const savedAlloc = await addAllocationMutation.mutateAsync({
          txId,
          allocation: alloc,
        });
        const idx = allocations.findIndex((a) => a === alloc);
        if (idx >= 0 && savedAlloc?.id) {
          setAllocations((prev) => {
            const updated = [...prev];
            const existing = updated[idx];
            if (existing) {
              updated[idx] = { ...existing, id: savedAlloc.id };
            }
            return updated;
          });
        }
      }

      message.success('Transaction saved');
      queryClient.invalidateQueries({ queryKey: ['bank-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['bank-transaction', txId] });
      
      if (transactionId) {
        setIsEditing(false);
      } else {
        onSaved?.();
      }
      return txId;
    } catch (error: any) {
      message.error(`Save failed: ${error.message}`);
      return null;
    }
  };

  const handleSaveAndReconcile = async () => {
    const txId = await handleSave();
    if (!txId) return;

    // Fetch saved transaction to get invoice numbers from xeroInvoice relation
    let invoiceNumbers = '';
    try {
      const savedTx = await bankTransactionsApi.get(txId) as any;
      invoiceNumbers = (savedTx?.allocations || [])
        .map((a: any) => a.xeroInvoice?.invoiceNumber)
        .filter(Boolean)
        .join(', ');
    } catch {
      invoiceNumbers = allocations
        .map(a => a.invoiceNumber)
        .filter(Boolean)
        .join(', ');
    }
    
    const clipboardText = invoiceNumbers || `Amount: $${Math.abs(transactionAmount).toLocaleString()}`;
    
    try {
      await navigator.clipboard.writeText(clipboardText);
      message.success(`Copied "${clipboardText}" to clipboard`);
    } catch {
      message.info(`Invoice: ${clipboardText}`);
    }

    try {
      const response = await bankTransactionsApi.getXeroReconcileLink();
      if (response.data?.deepLink) {
        window.open(response.data.deepLink, '_blank');
      }
    } catch {
      message.warning('Could not open Xero. Please navigate manually.');
    }
  };

  const isFeeAllocation = (alloc: Allocation) => 
    alloc.allocationType === 'BANK_FEE' || alloc.allocationType === 'ADJUSTMENT' || !alloc.shipmentId;

  const allocationColumns: ColumnsType<Allocation> = [
    {
      title: 'Shipment',
      key: 'shipment',
      render: (_: unknown, record: Allocation) => {
        if (isFeeAllocation(record)) {
          return (
            <div>
              <Tag color="orange">{record.shipmentRef || 'Fee/Adjustment'}</Tag>
              {record.notes && (
                <div style={{ fontSize: 11, color: colors.textSecondary }}>
                  {record.notes}
                </div>
              )}
            </div>
          );
        }
        return (
          <div>
            <div style={{ fontWeight: 600 }}>{record.shipmentRef}</div>
            {record.contractRef && (
              <div style={{ fontSize: 11, color: colors.textSecondary }}>
                {record.contractRef}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Party',
      key: 'party',
      render: (_: unknown, record: Allocation) => record.buyerName || record.supplierName || '-',
    },
    {
      title: 'Invoice',
      key: 'invoice',
      width: 250,
      render: (_: unknown, record: Allocation, index: number) => {
        if (isFeeAllocation(record)) {
          return <Text type="secondary">N/A</Text>;
        }

        return (
          <InvoiceSelect
            shipmentId={record.shipmentId}
            value={record.xeroInvoiceId}
            initialOptions={record.availableInvoices}
            onChange={(invoiceId, invoice) => {
              const updated = [...allocations];
              if (updated[index]) {
                updated[index].xeroInvoiceId = invoiceId;
                updated[index].invoiceNumber = invoice?.invoiceNumber;
                
                if (invoiceId && invoice && updated[index].amount === 0) {
                   updated[index].amount = invoice.amountDue;
                }
                
                setAllocations(updated);
              }
            }}
          />
        );
      },
    },
    {
      title: 'Type',
      dataIndex: 'allocationType',
      key: 'type',
      width: 150,
      render: (type: string, record: Allocation, index: number) => (
        <Select
          value={type}
          size="small"
          style={{ width: '100%' }}
          onChange={(value) => {
            const updated = [...allocations];
            if (updated[index]) {
              updated[index].allocationType = value;
              setAllocations(updated);
            }
          }}
        >
          {allocationTypes.map((t) => (
            <Option key={t.value} value={t.value}>
              {t.label}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 130,
      render: (amount: number, _: Allocation, index: number) => (
        <InputNumber
          value={amount}
          onChange={(value) => handleUpdateAmount(index, value || 0)}
          precision={2}
          prefix="$"
          size="small"
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: '',
      key: 'action',
      width: 50,
      render: (_: unknown, __: Allocation, index: number) => (
        <Popconfirm
          title="Remove this allocation?"
          onConfirm={() => handleRemoveAllocation(index)}
        >
          <Button type="text" danger icon={<DeleteOutlined />} size="small" />
        </Popconfirm>
      ),
    },
  ];

  // View-only columns for non-editing mode
  const viewAllocationColumns: ColumnsType<Allocation> = [
    {
      title: 'Shipment',
      key: 'shipment',
      render: (_: unknown, record: Allocation) => {
        if (isFeeAllocation(record)) {
          return (
            <div>
              <Tag color="orange">{record.shipmentRef || 'Fee/Adjustment'}</Tag>
              {record.notes && (
                <CopyableText value={record.notes}>
                  <div style={{ fontSize: 11, color: colors.textSecondary }}>
                    {record.notes}
                  </div>
                </CopyableText>
              )}
            </div>
          );
        }
        return (
          <div>
            <CopyableText value={record.shipmentRef}>
              <div style={{ fontWeight: 600 }}>{record.shipmentRef}</div>
            </CopyableText>
            {record.contractRef && (
              <CopyableText value={record.contractRef}>
                <div style={{ fontSize: 11, color: colors.textSecondary }}>
                  {record.contractRef}
                </div>
              </CopyableText>
            )}
          </div>
        );
      },
    },
    {
      title: 'Party',
      key: 'party',
      render: (_: unknown, record: Allocation) => {
        const party = record.buyerName || record.supplierName;
        return <CopyableText value={party}>{party || '-'}</CopyableText>;
      },
    },
    {
      title: 'Invoice',
      key: 'invoice',
      width: 180,
      render: (_: unknown, record: Allocation) => {
        if (isFeeAllocation(record)) {
          return <Text type="secondary">N/A</Text>;
        }
        return record.invoiceNumber ? (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <CopyableText value={record.invoiceNumber}>
              <Tag color="blue">{record.invoiceNumber}</Tag>
            </CopyableText>
            {record.xeroId && (
              <Tooltip title="Open in Xero">
                <a
                  href={`https://go.xero.com/AccountsReceivable/View.aspx?InvoiceID=${record.xeroId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: colors.textSecondary,
                    marginLeft: 4,
                    display: 'flex',
                    alignItems: 'center',
                    opacity: 0.7,
                    transition: 'opacity 0.2s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.7')}
                >
                  <LinkOutlined />
                </a>
              </Tooltip>
            )}
          </div>
        ) : (
          <Text type="secondary">No invoice</Text>
        );
      },
    },
    {
      title: 'Type',
      dataIndex: 'allocationType',
      key: 'type',
      width: 150,
      render: (type: string) => {
        const typeLabel = allocationTypes.find(t => t.value === type)?.label || type;
        return <Tag>{typeLabel}</Tag>;
      },
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 130,
      align: 'right',
      render: (amount: number) => {
        const isNegative = amount < 0;
        const absAmount = Math.abs(amount);
        const formatted = isNegative
          ? `($${absAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })})`
          : `$${absAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

        return (
          <CopyableText value={formatted}>
            <Text strong style={{ color: isNegative ? colors.error : undefined }}>
              {formatted}
            </Text>
          </CopyableText>
        );
      },
    },
  ];

  if (loadingTransaction) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <Spin size="large" />
      </div>
    );
  }

  const isPushed = existingTransaction?.status === 'PUSHED';

  return (
    <div>
      {/* Transaction Details */}
      <Card
        title={
          <Space>
            <BankOutlined />
            <span>{transactionId ? 'Bank Transaction' : 'New Bank Transaction'}</span>
            {existingTransaction?.status && (
              <Tag
                color={
                  existingTransaction.status === 'PUSHED'
                    ? 'green'
                    : existingTransaction.status === 'RECONCILED'
                      ? 'blue'
                      : 'default'
                }
              >
                {existingTransaction.status}
              </Tag>
            )}
          </Space>
        }
        extra={
          transactionId && !isPushed && (
            isEditing ? (
              <Space>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={handleSave}
                  loading={createMutation.isPending || addAllocationMutation.isPending}
                >
                  Save
                </Button>
                <Button
                  icon={<CloseOutlined />}
                  onClick={() => setIsEditing(false)}
                >
                  Cancel
                </Button>
              </Space>
            ) : (
              <Button icon={<EditOutlined />} onClick={() => setIsEditing(true)}>
                Edit
              </Button>
            )
          )
        }
        style={{ marginBottom: 16 }}
      >
        {isEditing ? (
          <Form form={form} layout="vertical" disabled={isPushed}>
            <Row gutter={16}>
              <Col span={6}>
                <Form.Item
                  name="transactionType"
                  label="Transaction Type"
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <Select placeholder="Select type">
                    {transactionTypes.map((t) => (
                      <Option key={t.value} value={t.value}>
                        {t.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="amount"
                  label="Amount"
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <InputNumber
                    prefix="$"
                    precision={2}
                    style={{ width: '100%' }}
                    min={0.01}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="date"
                  label="Date"
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="reference" label="Reference/TRN">
                  <Input placeholder="Wire reference number" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="counterparty" label="Counterparty">
                  <Input placeholder="Payer or beneficiary name" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="bankAccount" label="Bank Account">
                  <Select placeholder="Select bank account" allowClear>
                    {bankAccounts.map((account) => (
                      <Option key={account.id} value={account.id}>
                        {account.name}
                        {account.currencyCode && ` (${account.currencyCode})`}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="description" label="Description">
                  <Input placeholder="Optional notes" />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        ) : (
          <>
            <Row gutter={16}>
              <Col span={6}>
                <CopyableValue
                  label="Transaction Type"
                  value={transactionTypes.find(t => t.value === existingTransaction?.transactionType)?.label}
                />
              </Col>
              <Col span={6}>
                <CopyableValue
                  label="Amount"
                  value={existingTransaction?.amount ? Number(existingTransaction.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : null}
                  prefix="$"
                />
              </Col>
              <Col span={6}>
                <CopyableValue
                  label="Date"
                  value={existingTransaction?.date ? dayjs(existingTransaction.date).format('MMM D, YYYY') : null}
                />
              </Col>
              <Col span={6}>
                <CopyableValue
                  label="Reference/TRN"
                  value={existingTransaction?.reference}
                />
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <CopyableValue
                  label="Counterparty"
                  value={existingTransaction?.counterparty}
                />
              </Col>
              <Col span={8}>
                <CopyableValue
                  label="Bank Account"
                  value={bankAccounts.find(a => a.id === existingTransaction?.bankAccount)?.name || existingTransaction?.bankAccount}
                />
              </Col>
              <Col span={8}>
                <CopyableValue
                  label="Description"
                  value={existingTransaction?.description}
                />
              </Col>
            </Row>
          </>
        )}
      </Card>

      {/* Running Total */}
      <div style={{ marginBottom: 16 }}>
        <RunningTotalDisplay
          transactionAmount={transactionAmount}
          allocatedAmount={allocatedAmount}
        />
      </div>

      {/* Allocations */}
      <Row gutter={16}>
        <Col span={isEditing ? 16 : 24}>
          <Card
            title={
              <Space>
                <span>Allocations</span>
                <Tag>{allocations.length}</Tag>
              </Space>
            }
            extra={
              isEditing && !isPushed && (
                <Space>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setShipmentSearchVisible(true)}
                  >
                    Add Shipment
                  </Button>
                  <Button
                    icon={<DollarOutlined />}
                    onClick={handleAddBankFee}
                  >
                    Add Fee
                  </Button>
                </Space>
              )
            }
          >
            {allocations.length === 0 ? (
              <Empty
                description="No allocations yet"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              >
                {isEditing && (
                  <Button
                    type="primary"
                    icon={<SearchOutlined />}
                    onClick={() => setShipmentSearchVisible(true)}
                  >
                    Search Shipment
                  </Button>
                )}
              </Empty>
            ) : (
              <Table
                dataSource={allocations}
                columns={isEditing ? allocationColumns : viewAllocationColumns}
                rowKey={(_, index) => index?.toString() || '0'}
                size="small"
                pagination={false}
              />
            )}
          </Card>
        </Col>

        {isEditing && (
          <Col span={8}>
            <AvailableCreditsPanel
              partyName={allocations[0]?.buyerName || allocations[0]?.supplierName}
              onApplyCredit={handleApplyCredit}
            />
          </Col>
        )}
      </Row>

      {isEditing && (
        <Card style={{ marginTop: 16 }}>
          <Space>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={createMutation.isPending || addAllocationMutation.isPending}
              disabled={isPushed}
            >
              Save
            </Button>

            <Button
              type="primary"
              icon={<CloudUploadOutlined />}
              onClick={handleSaveAndReconcile}
              disabled={!isBalanced}
              loading={createMutation.isPending || addAllocationMutation.isPending}
              style={{ backgroundColor: isBalanced ? colors.success : undefined }}
            >
              Save & Reconcile in Xero
            </Button>

            {!isBalanced && (
              <Text type="warning">
                <ExclamationCircleOutlined style={{ marginRight: 4 }} />
                Allocations must equal transaction amount
              </Text>
            )}
          </Space>
        </Card>
      )}

      {/* Modals */}
      <ShipmentSearchModal
        visible={shipmentSearchVisible}
        onClose={() => setShipmentSearchVisible(false)}
        onSelect={handleAddShipment}
        excludeIds={allocations.map((a) => a.shipmentId)}
      />

      {savedTransactionId && (
        <XeroPushDialog
          visible={xeroPushVisible}
          transactionId={savedTransactionId}
          onClose={() => setXeroPushVisible(false)}
          onConfirm={() => pushToXeroMutation.mutate(savedTransactionId)}
        />
      )}
    </div>
  );
};

export default BankTransactionAllocation;
